import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { ArrowRight } from "@phosphor-icons/react";
import { HeroScreenshot, BentoLayout, AsymmetricGrid, ScreenshotPair } from "./ScreenshotLayouts";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const subsections = [
  {
    id: "meeting",
    title: "AI Meeting Infrastructure",
    teaser: "Automated meeting capture with Zoom/Meet/Teams bots, transcript pipelines, CRM synchronization, and integrations with Slack, Teams, and HubSpot.",
    image: "/screenshots/zime/zime-recordings.jpeg",
    narrative: [
      "Built and scaled AI-driven meeting intelligence workflows that automatically capture sales calls, process transcripts through proprietary LLM systems, and operationalize insights across coaching, forecasting, CRM synchronization, and sales execution workflows.",
    ],
    tags: ["Transcript Pipelines", "Integrations Ecosystem", "Ingestion Reliability", "CRM Sync Systems", "Automation Infrastructure"],
    layout: "hero+bento",
    hero: { src: "/screenshots/zime/zime-recordings.jpeg", alt: "Zime Recordings Dashboard", url: "app.zime.ai/recordings", caption: "Recordings dashboard — 2100+ calls with filters by type, date, speaker, deal, and transcript search" },
    bento: [
      { src: "/screenshots/zime/zime-integrations.jpeg", alt: "Platform Integrations", url: "app.zime.ai/settings", caption: "Integrations hub — Slack, Teams, HubSpot, and insight delivery configuration" },
      { src: "/screenshots/zime/zime-calls-report.jpeg", caption: "Calls & recordings report — recording metrics, trends, and calendar sync status" },
      { src: "/screenshots/zime/zime-crm-overview.jpeg", caption: "CRM integration overview — deal mapping stats across 3,600+ calls" },
    ],
  },
  {
    id: "ops",
    title: "Operational Intelligence Systems",
    teaser: "AI-driven deal insights, objection handling analytics, behavioral adoption scoring, and coaching intelligence that turned signals into action.",
    image: "/screenshots/zime/zime-objection-handling.jpeg",
    narrative: [
      "One of the systems I'm most proud of was a configurable checklist adoption and operational intelligence layer designed for enterprise sales organizations. The platform evaluated sales calls against customizable AI-driven checklists covering pricing alignment, next steps, technical readiness, objection handling, upsell opportunities, and qualification signals.",
      "While users found individual AI insights valuable, I identified a gap between having insights and actually driving consistent sales behavior across organizations.",
      "I led the initiative to operationalize these signals through behavioral adoption analytics that measured how consistently reps applied successful behaviors across deals, teams, and time periods.",
      "The work involved product reasoning, operational workflow thinking, UX direction, adoption logic design, customer validation, engineering coordination, enterprise configurability, and balancing flexibility with usability. This system became an operational feedback loop rather than just another passive dashboard.",
    ],
    tags: ["Product Reasoning", "Adoption Logic Design", "Customer Validation", "Enterprise Configurability", "Behavioral Analytics"],
    layout: "asymmetric",
    screenshots: [
      { src: "/screenshots/zime/zime-objection-handling.jpeg", alt: "Objection Handling Analytics", caption: "Objection handling — categorized by type and severity with drill-down to call evidence" },
      { src: "/screenshots/zime/zime-deal-insights.jpeg", alt: "AI Deal Insights", caption: "AI deal insights — momentum, stage confidence, ROI metrics, and action items" },
      { src: "/screenshots/zime/zime-ask-coaching.jpeg", alt: "Ask Zime AI Coaching", caption: "Ask Zime — conversational AI for real-time sales coaching and objection techniques" },
    ],
  },
  {
    id: "backend",
    title: "Backend & Platform Evolution",
    teaser: "Monolith to NestJS microservices with RabbitMQ, Elasticsearch analytics, and CRM ingestion pipelines — without disrupting enterprise users.",
    image: "/screenshots/zime/zime-crm-config.jpeg",
    narrative: [
      "Led the backend modernization effort alongside two junior engineers, gradually evolving the platform from tightly coupled systems toward a modular NestJS microservice architecture powered by RabbitMQ-based asynchronous workflows.",
      "The migration could not disrupt existing enterprise users or active frontend workflows, so a large part of the work involved incremental migration strategies, maintaining backward compatibility, identifying architectural boundaries, operational continuity, async vs sync tradeoffs, and coordination with product and customer success teams around mission-critical workflows.",
      "Also redesigned ingestion pipelines and analytics systems using Elasticsearch to reduce latency and improve scalability across high-volume operational datasets.",
    ],
    tags: ["Incremental Migration", "Backward Compatibility", "Async Workflows", "Elasticsearch", "Analytics Infrastructure"],
    layout: "pair",
    screenshots: [
      { src: "/screenshots/zime/zime-crm-config.jpeg", alt: "CRM Updates Configuration", url: "Admin controls", caption: "CRM sync configuration — automatic/manual update modes with entity-level sync targets" },
      { src: "/screenshots/zime/zime-onboarding.jpeg", alt: "Platform Onboarding", url: "Company onboarding", caption: "Company onboarding flow — CRM, calendar, notetaker, AI config, and Keycloak setup" },
    ],
  },
  {
    id: "enterprise",
    title: "Enterprise Multi-Tenancy",
    teaser: "Organization-aware access control with Keycloak IAM, RBAC/ReBAC, multi-tenant isolation, and company-level admin controls.",
    image: "/screenshots/zime/zime-companies-admin.jpeg",
    narrative: [
      "As enterprise pilots and larger customers onboarded, the platform evolved from a single-tenant product into a multi-tenant architecture with organization-aware access control and enterprise-grade security requirements.",
      "After researching scalable approaches, I led the migration toward a Keycloak-powered authentication and authorization system supporting RBAC, ReBAC, organization-aware onboarding, multi-tenant access isolation, and enterprise security workflows.",
      "The migration required carefully evolving authentication systems without disrupting existing onboarding and operational flows.",
    ],
    tags: ["RBAC / ReBAC", "Multi-Tenant Isolation", "Keycloak Auth", "Enterprise Security", "Org-Aware Onboarding"],
    layout: "pair",
    screenshots: [
      { src: "/screenshots/zime/zime-companies-admin.jpeg", alt: "Multi-Tenant Companies Admin", url: "Admin controls", caption: "Companies admin — multi-tenant management with CRM health, user counts, and engagement metrics" },
      { src: "/screenshots/zime/zime-onboarding.jpeg", alt: "Enterprise Onboarding", url: "Company onboarding", caption: "Enterprise onboarding — 7-step flow including Keycloak setup and user program configuration" },
    ],
  },
];

const ScreenshotRenderer = ({ data }) => (
  <>
    {data.layout === "hero+bento" && (
      <>
        <HeroScreenshot src={data.hero.src} alt={data.hero.alt} url={data.hero.url} caption={data.hero.caption} />
        <BentoLayout images={data.bento} />
      </>
    )}
    {data.layout === "asymmetric" && <AsymmetricGrid images={data.screenshots} />}
    {data.layout === "pair" && <ScreenshotPair images={data.screenshots} />}
  </>
);

const SubsectionCard = ({ item, onClick }) => (
  <motion.button
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    onClick={onClick}
    className="text-left bg-[#121212] border border-white/[0.08] rounded-xl overflow-hidden hover:border-white/[0.18] transition-all duration-300 group"
    data-testid={`zime-card-${item.id}`}
  >
    <div className="aspect-[16/9] overflow-hidden bg-[#1C1C1C]">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
      />
    </div>
    <div className="p-5">
      <h3 className="font-heading text-base font-semibold text-[#EDEDED] tracking-tight mb-2 group-hover:text-white transition-colors">
        {item.title}
      </h3>
      <p className="text-[13px] text-[#A1A1AA]/70 leading-relaxed line-clamp-2 mb-3">
        {item.teaser}
      </p>
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#38bdf8] group-hover:gap-2 transition-all">
        View details
        <ArrowRight size={12} weight="bold" />
      </span>
    </div>
  </motion.button>
);

export const CaseStudyZime = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [openSheet, setOpenSheet] = useState(null);

  const activeItem = subsections.find((s) => s.id === openSheet);

  return (
    <div ref={ref} className="mb-24" data-testid="case-study-zime">
      <motion.div
        initial="initial"
        animate={isInView ? "animate" : "initial"}
        transition={{ staggerChildren: 0.1 }}
        className="mb-10"
      >
        <motion.div
          variants={fadeUp}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 mb-5"
        >
          <span className="text-xs font-mono text-[#38bdf8] tracking-wider uppercase">Case Study 01</span>
          <span className="flex-1 h-px bg-white/[0.06]" />
        </motion.div>

        <motion.h2
          variants={fadeUp}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          data-testid="zime-heading"
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EDEDED] tracking-tight mb-3"
        >
          Zime.AI
        </motion.h2>

        <motion.p
          variants={fadeUp}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-base sm:text-lg text-[#A1A1AA] max-w-3xl leading-relaxed mb-4"
        >
          AI-powered operational intelligence for enterprise sales teams.
        </motion.p>

        <motion.p
          variants={fadeUp}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-sm text-[#A1A1AA]/70 max-w-3xl leading-relaxed"
        >
          Automatically captures sales calls via meeting bots integrated with Zoom, Meet, and Teams, processes conversations through proprietary LLM workflows, and generates operational insights around coaching, forecasting, pipeline reviews, and rep performance.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {subsections.map((item) => (
          <SubsectionCard key={item.id} item={item} onClick={() => setOpenSheet(item.id)} />
        ))}
      </div>

      <Sheet open={!!openSheet} onOpenChange={(open) => { if (!open) setOpenSheet(null); }}>
        <SheetContent
          side="right"
          className="!w-full sm:!w-[85vw] sm:!max-w-3xl bg-[#0A0A0A] border-l border-white/[0.06] p-0"
          data-testid="zime-detail-sheet"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{activeItem?.title || "Case Study Detail"}</SheetTitle>
            <SheetDescription>Zime.AI case study detail</SheetDescription>
          </SheetHeader>
          {activeItem && (
            <ScrollArea className="h-full">
              <div className="p-6 sm:p-10">
                <span className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-3">
                  Zime.AI
                </span>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#EDEDED] tracking-tight mb-6">
                  {activeItem.title}
                </h2>
                <div className="text-[15px] text-[#A1A1AA] leading-relaxed space-y-4 mb-6">
                  {activeItem.narrative.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {activeItem.tags.map((t) => (
                    <span key={t} className="text-[11px] font-mono px-3 py-1 rounded-full border border-white/[0.08] text-[#A1A1AA] bg-white/[0.02]">{t}</span>
                  ))}
                </div>
                <ScreenshotRenderer data={activeItem} />
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
